import type {
  SystemRepairDefinition,
  SystemRepairHandler,
  SystemRepairRegistration,
} from './SystemWorkspaceContracts';

const registrations = new Map<
  string,
  SystemRepairRegistration
>();

function registrationKey(
  systemId: string,
  repairId: string,
): string {
  return `${systemId}:${repairId}`;
}

export function registerSystemRepair(
  definition: SystemRepairDefinition,
  handler: SystemRepairHandler,
): void {
  if (
    definition.id !==
    handler.repairId
  ) {
    throw new Error(
      'Repair definition and handler IDs do not agree.',
    );
  }

  if (
    definition.systemId !==
    handler.systemId
  ) {
    throw new Error(
      'Repair definition and handler system authority do not agree.',
    );
  }

  if (definition.risk === 'prohibited') {
    throw new Error(
      'Prohibited repairs cannot be registered.',
    );
  }

  if (!definition.idempotent) {
    throw new Error(
      'Registered repairs must be idempotent.',
    );
  }

  if (!definition.reversible) {
    throw new Error(
      'Registered repairs must be reversible.',
    );
  }

  if (
    definition.verificationRequirements
      .length === 0
  ) {
    throw new Error(
      'Registered repairs require verification.',
    );
  }

  if (
    definition.rollbackRequirements
      .length === 0
  ) {
    throw new Error(
      'Registered repairs require rollback.',
    );
  }

  const key = registrationKey(
    definition.systemId,
    definition.id,
  );

  if (registrations.has(key)) {
    throw new Error(
      `System repair already registered: ${key}`,
    );
  }

  registrations.set(key, {
    definition,
    handler,
  });
}

export function getSystemRepairRegistration(
  systemId: string,
  repairId: string,
): SystemRepairRegistration | null {
  return (
    registrations.get(
      registrationKey(
        systemId,
        repairId,
      ),
    ) ?? null
  );
}

export function listSystemRepairRegistrations(
  systemId?: string,
): SystemRepairRegistration[] {
  const values = [
    ...registrations.values(),
  ];

  if (!systemId) {
    return values;
  }

  return values.filter(
    registration =>
      registration.definition.systemId ===
      systemId,
  );
}

export function hasSystemRepairRegistration(
  systemId: string,
  repairId: string,
): boolean {
  return registrations.has(
    registrationKey(
      systemId,
      repairId,
    ),
  );
}

export function clearSystemRepairRegistryForTests():
  void {
  registrations.clear();
}
